import { DestroyRef, Injectable } from '@angular/core';
import { of, shareReplay } from 'rxjs';
import { AdjustedPeriod, RestaurantWorkPeriod } from '../models';
import { Intercom } from './intercom.service';
import { debounceTime, filter, map, switchMap, take } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { WatchdogService } from './watchdog.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WebsocketService } from './websocket.service';
import { KVRepository } from '../repositories';
import { ClusterService } from './cluster.service';
import { isTruthy } from '../utils';

@Injectable()
export class DeviceWorkScheduleService {

  private readonly storageKey = 'restaurantWorkSchedule';
  private readonly logger = this.watchdog.tag('Restaurant Work Schedule', 'cyan');

  private readonly schedule$ = this.kvRepository.one$(this.storageKey).pipe(
    map((data) => {
      if (data?.value) {
        return data.value as RestaurantWorkPeriod[];
      }

      return null;
    }),
    shareReplay(1),
  );

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly auth: AuthService,
    private readonly cluster: ClusterService,
    private readonly intercom: Intercom,
    private readonly kvRepository: KVRepository,
    private readonly watchdog: WatchdogService,
    private readonly webSocket: WebsocketService,
  ) {}

  public initialize(): void {
    this.logger.info('Initialize');

    // Send the work schedule to the device
    this.cluster.leader$.pipe(
      filter(isTruthy),
      switchMap(() => this.intercom.connected$),
      filter(isTruthy),
      switchMap(() => this.auth.authorized$),
      switchMap((authorized) => {
        if (!authorized) {
          return of(null);
        }

        return this.schedule$;
      }),
      map((schedule) => schedule ?? []),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((schedule) => {
      this.intercom.call('device.work_schedule_periods_v2', schedule);
    });

    // Retry sending the work schedule to the device
    this.cluster.leader$.pipe(
      filter(isTruthy),
      switchMap(() => this.intercom.messages$),
      filter((message) => message.event === 'device.work_schedule_periods.failed'),
      debounceTime(5000),
      switchMap(() => this.schedule$),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((schedule) => {
      this.intercom.call('device.work_schedule_periods_v2', schedule ?? []);
    });

    // Update the work schedule when it changes
    this.cluster.leader$.pipe(
      filter(isTruthy),
      switchMap(() => this.webSocket.messages$),
      filter((response) => response.type === 'tableInfo'),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((response) => {
      this.logger.info('Received: TableInfo message.', response);

      if (response.data.restaurantSchedulePeriods) {
        this.update(response.data.restaurantSchedulePeriods);
      }

      if (response.data.deviceSchedulePeriods) {
        this.intercom.call('device.work_schedule_periods', []);
      }
    });
  }

  public update(schedules: RestaurantWorkPeriod[]): void {
    this.schedule$.pipe(
      take(1),
      switchMap((scheduleLocal) => {

        if (schedules?.length > 0) {
          const newSchedule = schedules
            .filter((schedule) => schedule.active)
            .map((schedule) => {
              return this.applyPeriodOffset(schedule);
            })
            .filter((schedule): schedule is NonNullable<typeof schedule> => schedule !== null);

          if (!this.areSchedulesEqual(scheduleLocal, newSchedule)) {
            return this.kvRepository.update$({
              key: this.storageKey,
              value: newSchedule,
            });
          } else {
            this.intercom.call('device.work_schedule_periods_v2', newSchedule);
            return of(newSchedule);
          }
        }

        return this.kvRepository.delete$(this.storageKey);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  private areSchedulesEqual(
    scheduleA: AdjustedPeriod[] | null,
    scheduleB: AdjustedPeriod[]
  ): boolean {
    if (!scheduleA || scheduleA.length !== scheduleB.length) {
      return false;
    }

    const sortedA = [...scheduleA].sort((a, b) => a.id - b.id);
    const sortedB = [...scheduleB].sort((a, b) => a.id - b.id);

    return sortedA.every((a, index) => {
      const b = sortedB[index];
      return (
        a.id === b.id &&
        a.startDay === b.startDay &&
        a.startTime === b.startTime &&
        a.endDay === b.endDay &&
        a.endTime === b.endTime
      );
    });
  }

  private applyPeriodOffset(period: RestaurantWorkPeriod): AdjustedPeriod | null{

    if (!period.autoOn && !period.autoOff) {
      return null
    }

    const offsetStart = period.autoOn
      ? this.applyOffsetWithDay(period.startTime, period.startDay, period.autoOnOffset)
      : null;

    const offsetEnd = period.autoOff
      ? this.applyOffsetWithDay(period.endTime, period.endDay, period.autoOffOffset)
      : null;

    const newPeriod: AdjustedPeriod = {
      ...period,
      startTime: offsetStart ? offsetStart.time : null,
      startDay: offsetStart ? offsetStart.day : null,
      endTime: offsetEnd ? offsetEnd.time : null,
      endDay: offsetEnd ? offsetEnd.day : null
    };

    return newPeriod;
  }

  private applyOffsetWithDay(
    time: string,
    day: number,
    offsetSeconds: number
  ): { time: string; day: number } {
    const [hours, minutes] = time.split(':').map(Number);
    const totalSeconds = (hours * 60 + minutes) * 60 + offsetSeconds;

    const totalSecondsInWeek = 7 * 24 * 60 * 60;
    const adjustedSeconds = (totalSeconds + totalSecondsInWeek) % totalSecondsInWeek;

    const newDay = ((day - 1 + Math.floor(adjustedSeconds / (24 * 60 * 60))) % 7 + 7) % 7 + 1;
    const remainder = adjustedSeconds % (24 * 60 * 60);
    const newHour = Math.floor(remainder / 3600);
    const newMinute = Math.floor((remainder % 3600) / 60);

    return {
      time: `${newHour.toString().padStart(2, '0')}:${newMinute.toString().padStart(2, '0')}`,
      day: newDay
    };
  }

}
